import React from 'react'
import TooltipWrapper from '../../../components/general/TooltipWrapper'
import MenuContainer from '../../../hooks/MenuContainer'
import MenuItemModel from '../../../models/nlp/menu/MenuItemModel'
import styled from '@emotion/styled';
const pushPinActiveIcon = require('../../../content/images/menu-editor/push-pin-active.svg');
const pushPinInactiveIcon = require('../../../content/images/menu-editor/push-pin-inactive.svg');

interface PersistentPropertyTitleProps {
    title?: string
    showTitle?: boolean
    item: MenuItemModel | MenuItemModel["attributes"]
    propertyName: string
}

export default function PersistentPropertyTitle(props: PersistentPropertyTitleProps) {
    const menuContainer = MenuContainer.useContainer();
    const isPersitent = props.item.persistentProperties?.includes(props.propertyName);
    const isLocationSpecificPersistent = props.item.locationSpecificPersistentProperties?.includes(props.propertyName);

    let persistentPropertyToolTipText =
        <TooltipText>
            <TooltipTitle>General persistent property</TooltipTitle>
            <span>The "{props.title}" value
            <PersistenPropertyVerb>
                {isPersitent ? "won't" : "might"}
            </PersistenPropertyVerb>
            update when you pull the latest menu from your POS. This is the shared setting used across locations. Click to change.</span>
        </TooltipText>

    let locationSpecificPersistentPropertyToolTipText =
        <TooltipText>
            <TooltipTitle>Location-specific persistent property</TooltipTitle>
            <span>The "{props.title}" value
            <PersistenPropertyVerb>
                {isLocationSpecificPersistent ? "won't" : "might"}
            </PersistenPropertyVerb>
            update when you pull the latest menu from your POS for this specific location. These location-specific settings do not carry over when syncing manual changes from the primary location. Click to change.</span>
        </TooltipText>

    return (
        <PersistenPropertyDiv>
            {props.title && props.showTitle !== false && <Title>{props.title}</Title>}
            <PersistentActions>
            {
                <TooltipWrapper
                    place="right"
                    text={persistentPropertyToolTipText}
                    maxWidth={300}
                >
                    <PersistentPropertyButton
                        isActive={isPersitent}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (props.item.persistentProperties?.includes(props.propertyName)) {
                                const index = props.item.persistentProperties.findIndex(p => p === props.propertyName)
                                if (index >= 0) {
                                    props.item.persistentProperties.splice(index, 1);
                                }
                            } else {
                                if (!props.item.persistentProperties) props.item.persistentProperties = [];
                                props.item.persistentProperties.push(props.propertyName)
                            }
                            menuContainer.setMenuItem({ ...menuContainer.menuItem });
                        }}
                    >
                        <PersistentPropertyIcon src={isPersitent ? pushPinActiveIcon : pushPinInactiveIcon} />
                        <PersistentPropertyLabel>General</PersistentPropertyLabel>
                    </PersistentPropertyButton>
                </TooltipWrapper>
            }
            {
                <TooltipWrapper
                    place="right"
                    text={locationSpecificPersistentPropertyToolTipText}
                    maxWidth={340}
                >
                    <LocationPersistentPropertyButton
                        isActive={isLocationSpecificPersistent}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (props.item.locationSpecificPersistentProperties?.includes(props.propertyName)) {
                                const index = props.item.locationSpecificPersistentProperties.findIndex(p => p === props.propertyName)
                                if (index >= 0) {
                                    props.item.locationSpecificPersistentProperties.splice(index, 1);
                                }
                            } else {
                                if (!props.item.locationSpecificPersistentProperties) props.item.locationSpecificPersistentProperties = [];
                                props.item.locationSpecificPersistentProperties.push(props.propertyName)
                            }
                            menuContainer.setMenuItem({ ...menuContainer.menuItem });
                        }}
                    >
                        <PersistentPropertyIcon src={isLocationSpecificPersistent ? pushPinActiveIcon : pushPinInactiveIcon} />
                        <PersistentPropertyLabel>Location only</PersistentPropertyLabel>
                    </LocationPersistentPropertyButton>
                </TooltipWrapper>
            }
            </PersistentActions>
        </PersistenPropertyDiv>
    )
}

const PersistenPropertyDiv = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
`;
const Title = styled.span`
    margin-right: 5px;
`;

const PersistentActions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const PersistentPropertyButton = styled.div<{ isActive?: boolean }>`
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 2px 6px;
    border-radius: 4px;
    background: ${props => props.isActive ? '#f1f5f9' : 'transparent'};
    color: ${props => props.isActive ? '#2b6cb0' : '#8898aa'};
    font-size: 11px;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
    transition: background 0.15s ease-in-out, color 0.15s ease-in-out;

    &:hover {
        background: ${props => props.isActive ? '#e2e8f0' : '#f8fafc'};
        color: ${props => props.isActive ? '#2b6cb0' : '#526274'};
    }
`;

const LocationPersistentPropertyButton = styled(PersistentPropertyButton)`
    background: ${props => props.isActive ? '#fffaf2' : 'transparent'};
    color: ${props => props.isActive ? '#c77800' : '#8898aa'};

    &:hover {
        background: ${props => props.isActive ? '#fff4df' : '#f8fafc'};
        color: ${props => props.isActive ? '#c77800' : '#526274'};
    }

    img {
        ${props => props.isActive ? 'filter: sepia(100%) saturate(650%) hue-rotate(350deg);' : ''}
    }
`;

const PersistentPropertyIcon = styled.img`
    width: 13px;
    height: 13px;
    margin-right: 4px;
`;

const PersistentPropertyLabel = styled.span`
    display: inline-block;
`;

const PersistenPropertyVerb = styled.span`
    font-weight: bold;
    text-decoration: underline;
    margin: 0 5px;
`

const TooltipText = styled.span`
    display: inline-block;
`;

const TooltipTitle = styled.span`
    display: block;
    font-weight: bold;
    margin-bottom: 4px;
`;

